// =====================================================================
// Accidents.jsx — M-010 : registre des premiers soins et des accidents
// =====================================================================
// Module SEPARE des vérifications, à la demande de Florent (26/08).
//
// La raison n'est pas l'ordre du menu. Ce registre contient des DONNÉES
// DE SANTÉ. Quand son accès cohabite avec « nettoyage des tables de
// packing », la frontière devient floue — et la frontière est précisément
// ce qu'un inspecteur ou un contrôle RGPD vient regarder. Séparé, « qui
// peut ouvrir le registre » devient une question unique et évidente.
//
// Deuxième raison, pratique : celui qui trouve un collègue blessé ne doit
// pas chercher une tuile « Sécurité & routines » puis choisir un onglet.
// L'accident est rare, urgent, individuel, avec des délais légaux ; les
// routines sont hebdomadaires, collectives, prévisibles. Deux rythmes.
//
// Ce qui reste PARTAGÉ avec le module des vérifications : le plan, les
// objets, les actions correctives. Un seul autocollant sert les deux flux
// — séparer la scannerie obligerait à coller deux étiquettes par trousse.
// L'autocollant d'une trousse mène ici ; celui d'un extincteur mène aux
// vérifications ; chaque écran porte un lien vers l'autre, pour le cas où
// la personne s'est trompée de geste.
//
// Ce que l'écran garantit côté légal :
//   - la victime est pré-remplie avec l'utilisateur connecté, mais on peut
//     déclarer pour un collègue ou pour un tiers (intérimaire, visiteur)
//   - la liste « qui a donné les premiers soins » ne contient QUE des
//     secouristes à certification valide ; si elle est vide, l'écran le dit
//     au lieu de faire semblant (art. I.5-6 §1 — couverture)
//   - les témoins existent comme champ : la 5e mention obligatoire manque
//     dans le fichier Excel actuel
//   - le bouton SOS écrit noir sur blanc qu'il n'appelle PAS les secours
// =====================================================================

import { useState, useEffect, useCallback } from 'react';
import { safety, errMsg } from '../services/api.js';
import { useAuth } from '../hooks/useAuth.jsx';
import Breadcrumbs from '../components/Breadcrumbs.jsx';
import SecuriteRegistrePrint from './SecuriteRegistrePrint.jsx';
import AccidentsSecouristes from './AccidentsSecouristes.jsx';
import { codeScanne } from '../lib/codeScanne.js';
import { manquesCloture } from '../lib/accidents.js';
import { erreurLieu, LIBELLE_POINT } from '../lib/lieuPlan.js';
import AccidentsLieuPlan, { PointSurPlan } from './AccidentsLieuPlan.jsx';

const C = {
  line: '#e5e7eb', ink: '#3f4654', mute: '#7b8494', brand: '#714B67',
  ok: '#16a34a', okBg: '#effaf3', okLine: '#cfead9',
  warn: '#d97706', warnBg: '#fdf5e7',
  crit: '#dc2626', critBg: '#fdf0f0',
  bg: '#f8fafc',
};

const STATUS = {
  BROUILLON:          { label: 'brouillon',          bg: '#f1f5f9', fg: '#475569' },
  SOS_EN_COURS:       { label: 'SOS en cours',       bg: C.critBg,  fg: C.crit },
  REGISTRE_BENIN:     { label: 'registre (bénin)',   bg: C.okBg,    fg: '#065F46' },
  A_DECLARER_ASSUREUR: { label: 'à déclarer à l’assureur', bg: C.warnBg, fg: '#92400E' },
  DECLARE_ASSUREUR:   { label: 'déclaré assureur',   bg: '#e0e7ff', fg: '#3730a3' },
  AGGRAVE_A_DECLARER: { label: 'aggravé — à déclarer', bg: C.warnBg, fg: '#92400E' },
  CLOS:               { label: 'clos',               bg: '#f1f5f9', fg: '#475569' },
};

// Le journal est lu par des humains, y compris lors d'un controle :
// des libelles en clair, pas des constantes techniques.
const EVENT_LABEL = {
  CREATED: 'Inscription',
  UPDATED: 'Modification',
  SOS: 'SOS declenche',
  QUALIFIED: 'Qualification',
  AGGRAVATED: 'Aggravation',
  INSURER_DECLARED: 'Declare a l assureur',
  GRAVE_NOTIFIED: 'Inspection notifiee',
  REPORT_SENT: 'Rapport envoye',
  CLOSED: 'Cloture',
  CONSULTED: 'Consultation',
  EXPORTED: 'Export',
  NOTIFIED: 'Notification envoyee',
};

const SEVERITY = {
  A_QUALIFIER: { label: 'à qualifier', fg: C.warn },
  BENIN:       { label: 'bénin',       fg: C.ok },
  NON_BENIN:   { label: 'non bénin',   fg: C.warn },
  GRAVE:       { label: 'GRAVE',       fg: C.crit },
  TRES_GRAVE:  { label: 'TRÈS GRAVE',  fg: C.crit },
};

const inp = {
  width: '100%', padding: '0.6rem', border: `1px solid ${C.line}`,
  borderRadius: 6, fontSize: '1rem', background: '#fff', boxSizing: 'border-box',
};
const lbl = { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: C.mute, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.04em' };
const card = { background: '#fff', border: `1px solid ${C.line}`, borderRadius: 10, padding: '1rem 1.1rem', marginBottom: '1rem' };
const btn = (bg, fg = '#fff') => ({
  background: bg, color: fg, border: bg === '#fff' ? `1px solid ${C.line}` : '1px solid transparent',
  borderRadius: 7, padding: '0.65rem 1rem', fontSize: '0.95rem', fontWeight: 600,
  cursor: 'pointer', width: '100%',
});

function Badge({ map, value }) {
  const s = map[value] || { label: value, bg: '#f1f5f9', fg: '#475569' };
  return (
    <span style={{
      background: s.bg, color: s.fg, borderRadius: 999, padding: '2px 9px',
      fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap',
    }}>{s.label}</span>
  );
}

/** Le lieu d'une fiche, en clair : l'objet du QR, la précision écrite, ou le point sur le plan (24-10-2026). */
const lieuLisible = (f) => [f.asset ? `${f.asset.name} (${f.asset.code})` : null, f.locationText,
  !f.asset && !f.locationText && f.planId ? LIBELLE_POINT : null].filter(Boolean).join(' — ') || '—';

export default function Securite() {
  const { can, user } = useAuth();
  const canRead = can('safety.read.all');
  const canPlan = can('maintenance.plan.all');
  const canManage = can('safety.manage.all');

  const [tab, setTab] = useState('scan');
  const [flash, setFlash] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  // --- lieu et secouristes ---
  const [asset, setAsset] = useState(null);    // l'objet du QR, quand on arrive par l'autocollant d'une trousse
  const [aiders, setAiders] = useState([]);
  const [aidersWarning, setAidersWarning] = useState(null);
  // SOS de la personne encore sans détails : la déclaration le COMPLÈTE au lieu de
  // créer une seconde fiche (23-10-2026). Vient de l'ouverture de l'écran, du QR, ou du bouton SOS juste pressé.
  const [sosOuvert, setSosOuvert] = useState(null);
  const [lieu, setLieu] = useState(null);   // point touché sur le plan + texte proposé (24-10-2026)

  // --- formulaire de déclaration ---
  const empty = {
    description: '', circumstances: '', injuryNature: '', injurySite: '',
    careGiven: '', followUp: '', witnesses: '', locationText: '',
    firstAiderUserId: '', selfTreated: false,
    forOther: false, victimUserId: '', victimExternName: '', victimStatus: 'INTERNE',
  };
  const [form, setForm] = useState(empty);
  const set = (k) => (e) => setForm((f) => ({
    ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
  }));

  // --- aides à la saisie (servies par l'API) ---
  const [vocab, setVocab] = useState({});
  const [people, setPeople] = useState([]);

  // --- registre ---
  const [list, setList] = useState([]);
  const [deadlines, setDeadlines] = useState(null);
  const [sel, setSel] = useState(null);        // fiche ouverte (detail + journal)
  const [q, setQ] = useState({                 // formulaire de qualification
    severity: '', b1: false, b2: false, b3: false, b4: false, note: '',
  });
  const [act, setAct] = useState({ insurerRef: '', reason: '', reportNote: '' });
  const [showAll, setShowAll] = useState(false);   // ignorer le raffinement
  // Impression du registre : `null` = pas en impression, `{focus}` = une
  // seule fiche, `{}` = toute la periode.
  const [printing, setPrinting] = useState(null);

  // 24-10-2026 : on déclare sans rien scanner — les secouristes proposables et le SOS à compléter
  // arrivent dès l'ouverture (avant, seul le scan les donnait). Une réponse lente ne remplace pas
  // le SOS pressé entre-temps : sinon la déclaration créerait une seconde fiche.
  // Ce que renvoient le contexte ET un scan réussi : les secouristes et un SOS à compléter (+ l'objet du QR).
  const appliquerContexte = useCallback((r) => {
    if (r.asset) setAsset(r.asset);
    setAiders(r.firstAiders || []);
    setAidersWarning(r.firstAidersWarning || null);
    setSosOuvert((s) => s || r.openSos || null);
  }, []);
  const chargerContexte = useCallback(async () => {
    try { appliquerContexte(await safety.contexteDeclaration()); }
    catch (_) { /* sans le droit de déclarer, le serveur le dira à l'envoi */ }
  }, [appliquerContexte]);
  useEffect(() => { chargerContexte(); }, [chargerContexte]);

  // Arrivee par l'appareil photo du telephone : /accidents?scan=BS-01
  // On charge la fiche tout de suite et on nettoie l'URL, pour qu'un
  // rafraichissement ne rejoue pas un scan involontairement.
  useEffect(() => {
    const c = codeScanne(new URLSearchParams(window.location.search).get('scan'));
    if (!c) return;
    window.history.replaceState({}, '', window.location.pathname);
    setTab('scan');
    (async () => {
      setBusy(true); setErr(null);
      try {
        appliquerContexte(await safety.scan(c));
      } catch (e) {
        setErr(errMsg(e));
      } finally { setBusy(false); }
    })();
  }, []);   // une seule fois, au montage

  // Vocabulaire et personnes : chargés une fois, cotés serveur (on peut
  // enrichir les listes sans reconstruire le frontend).
  useEffect(() => {
    (async () => {
      try { setVocab(await safety.vocab()); } catch (_) { /* perm */ }
      try { setPeople(await safety.people()); } catch (_) { /* perm */ }
    })();
  }, []);

  const loadRegister = useCallback(async () => {
    if (!canRead) return;
    try {
      const [rows, dl] = await Promise.all([safety.list({ limit: 100 }), safety.deadlines()]);
      setList(rows); setDeadlines(dl);
    } catch (e) { setErr(errMsg(e)); }
  }, [canRead]);

  useEffect(() => { if (tab === 'registre') loadRegister(); }, [tab, loadRegister]);

  async function doSos() {
    setBusy(true); setErr(null);
    try {
      // 24-10-2026 : plus besoin d'avoir scanné ; le point du plan et la précision écrite partent
      // avec l'alerte s'ils sont déjà là (le mail dit alors « près de : … »).
      const r = await safety.sos({
        ...(asset ? { assetCode: asset.code } : {}),
        ...(lieu ? { planId: lieu.planId, planX: lieu.planX, planY: lieu.planY } : {}),
        ...(form.locationText.trim() ? { locationText: form.locationText.trim() } : {}),
      });
      setFlash({ kind: 'sos', text: `SOS envoyé — ${r.incident.ref}. ${r.notice}` });
      setSosOuvert({ id: r.incident.id, ref: r.incident.ref, sosAt: r.incident.sosAt });
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  }

  // « Pour quelqu'un d'autre » sans personne choisie inscrivait le DÉCLARANT comme victime :
  // la combinaison est refusée avant l'envoi, avec la même phrase que le serveur.
  const victimeManquante = form.forOther && (form.victimStatus === 'TIERS'
    ? !form.victimExternName.trim()
    : !form.victimUserId);

  async function doDeclare(e) {
    e.preventDefault();
    // Le message (refus ou « Inscrit au registre ») s'affiche en haut de page : on y remonte à CHAQUE envoi
    // (même message deux fois de suite compris), sinon la personne qui vient d'appuyer sur le bouton du bas
    // ne voit rien se passer (24-10-2026).
    const enHaut = () => window.scrollTo(0, 0);
    const pbLieu = erreurLieu({ asset, lieu, locationText: form.locationText });
    if (pbLieu) { setErr(pbLieu); enHaut(); return; }
    if (victimeManquante) {
      setErr('Choisissez la victime, ou décochez « je déclare pour quelqu\'un d\'autre ».');
      enHaut();
      return;
    }
    setBusy(true); setErr(null);
    try {
      const payload = {
        ...(asset ? { assetCode: asset.code } : {}),
        ...(lieu ? { planId: lieu.planId, planX: lieu.planX, planY: lieu.planY } : {}),
        locationText: form.locationText.trim() || null,
        description: form.description,
        circumstances: form.circumstances || null,
        injuryNature: form.injuryNature || null,
        injurySite: form.injurySite || null,
        careGiven: form.careGiven || null,
        followUp: form.followUp || null,
        witnesses: form.witnesses || null,
        victimStatus: form.victimStatus,
        selfTreated: form.selfTreated,
        firstAiderUserId: form.selfTreated ? null : (form.firstAiderUserId || null),
        forOther: form.forOther,
      };
      if (form.forOther) {
        if (form.victimStatus === 'TIERS') payload.victimExternName = form.victimExternName;
        else payload.victimUserId = form.victimUserId || null;
      }
      const inc = sosOuvert
        ? await safety.completeSos(sosOuvert.id, payload)
        : await safety.declare(payload);
      // Dire ce qui s'est vraiment passé : avant le 23-10-2026 l'écran affirmait
      // « les responsables sont prévenus » alors qu'aucun mail ne partait.
      const prevenus = inc.notification?.mail > 0
        ? ' Les responsables ont été prévenus par mail.'
        : ' Les responsables la verront dans le registre.';
      setFlash({ kind: 'ok', text: `${sosOuvert ? 'SOS complété' : 'Inscrit au registre'} — ${inc.ref}.${prevenus}` });
      setSosOuvert(null);
      setForm(empty);
      setAsset(null); setLieu(null);
      chargerContexte();
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); enHaut(); }
  }

  // =====================================================================
  // ===================================================================
  // Qualification et suites. Chaque action est refusee par l'API si elle
  // n'est pas legalement coherente — l'ecran ne fait que la proposer.
  // ===================================================================
  async function openFiche(id) {
    setErr(null);
    try {
      const f = await safety.get(id);            // GET journalise la consultation
      setSel(f);
      setQ({ severity: '', b1: false, b2: false, b3: false, b4: false, note: '' });
      setAct({ insurerRef: '', reason: '', reportNote: '' });
    } catch (e) { setErr(errMsg(e)); }
  }

  async function run(fn, okMsg) {
    setBusy(true); setErr(null);
    try {
      await fn();
      setFlash({ kind: 'ok', text: okMsg });
      await loadRegister();
      if (sel) await openFiche(sel.id);
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  }

  const fourChecked = q.b1 && q.b2 && q.b3 && q.b4;

  // Raffinement : la nature de la lesion reduit les listes suivantes.
  // `_refine` vient de l'API, donc on peut l'enrichir sans rebuild.
  const refine = (vocab._refine || {})[form.injuryNature] || null;
  const opts = (field) => {
    const full = vocab[field] || [];
    if (showAll || !refine || !refine[field]) return full;
    // on garde l'ordre du vocabulaire complet, filtre sur le sous-ensemble
    const keep = new Set(refine[field]);
    return full.filter((v) => keep.has(v));
  };
  const isRefined = (field) => Boolean(!showAll && refine && refine[field]);
  const beninExclu = Boolean(refine && refine.excludesBenin);

  // Fonction de rendu, PAS un composant : déclarée comme `function Fiche()` et rendue
  // `<Fiche />`, elle était recréée à chaque frappe — React démontait les champs, le
  // curseur sautait et le clavier du téléphone se fermait (23-10-2026).
  function fiche() {
    if (!sel) return null;
    const j = sel.events || [];
    const row = (k, v) => v ? (
      <div style={{ display: 'flex', gap: 10, padding: '3px 0', fontSize: '0.88rem' }}>
        <span style={{ color: C.mute, minWidth: 170 }}>{k}</span>
        <span>{v}</span>
      </div>
    ) : null;

    return (
      <div style={{ ...card, borderLeft: `4px solid ${C.brand}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
          <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>
            {sel.ref} <Badge map={STATUS} value={sel.status} />
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => setPrinting({ focus: sel.id })}
              style={{ ...btn('#fff', C.brand), width: 'auto', padding: '4px 10px' }}>
              Imprimer cette fiche
            </button>
            <button onClick={() => setSel(null)} style={{ ...btn('#fff', C.mute), width: 'auto', padding: '4px 10px' }}>
              Fermer
            </button>
          </div>
        </div>

        {row('Victime', `${sel.victimName || '—'}${sel.victimStatus ? ` (${sel.victimStatus.toLowerCase()})` : ''}`)}
        {row('Premiers soins par', sel.firstAiderName || '—')}
        {row('Lieu', lieuLisible(sel))}
        {sel.planId && <PointSurPlan planId={sel.planId} x={sel.planX} y={sel.planY} />}
        {row('Date et heure', String(sel.occurredAt).replace('T', ' ').slice(0, 16))}
        {row('Description', sel.description)}
        {row('Circonstances', sel.circumstances)}
        {row('Lésion', [sel.injuryNature, sel.injurySite].filter(Boolean).join(' · '))}
        {row('Soins', sel.careGiven)}
        {row('Suites', sel.followUp)}
        {row('Témoins', sel.witnesses)}
        {row('Déclarée par', sel.declaredByName)}

        {/* ---------------- QUALIFICATION ---------------- */}
        {canManage && sel.severity === 'A_QUALIFIER' && !sel.lockedAt && (
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.line}` }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Qualifier cette fiche</div>

            <div style={{ background: C.warnBg, border: '1px solid #f0dcb4', borderRadius: 6,
                          padding: '0.6rem 0.7rem', fontSize: '0.83rem', color: '#92400E', marginBottom: 10 }}>
              Un accident n'est <b>bénin</b> que si les <b>4 conditions</b> sont réunies. Une
              fracture, un arrêt de travail ou une consultation médicale excluent le bénin :
              dans ce cas l'accident se <b>déclare à l'assureur dans les 8 jours calendrier</b> qui
              suivent l'accident — grave ou non.
              {(vocab._refine || {})[sel.injuryNature]?.excludesBenin && (
                <div style={{ marginTop: 6, fontWeight: 700 }}>
                  Lésion déclarée : « {sel.injuryNature} » — normalement incompatible avec le bénin.
                </div>
              )}
            </div>

            <div style={{ display: 'grid', gap: 6, marginBottom: 10 }}>
              {[
                ['b1', 'Aucune perte de salaire'],
                ['b2', 'Aucune incapacité de travail'],
                ['b3', 'Soins donnés sans médecin'],
                ['b4', 'Soins dispensés sur place uniquement'],
              ].map(([k, l]) => (
                <label key={k} style={{ fontSize: '0.9rem', display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="checkbox" checked={q[k]}
                    onChange={(e) => setQ((x) => ({ ...x, [k]: e.target.checked }))} />
                  {l}
                </label>
              ))}
            </div>

            <input style={{ ...inp, marginBottom: 10 }} placeholder="Note de qualification (optionnel)"
              value={q.note} onChange={(e) => setQ((x) => ({ ...x, note: e.target.value }))} />

            <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))' }}>
              <button disabled={busy || !fourChecked} title={fourChecked ? '' : 'Les 4 conditions doivent être cochées'}
                onClick={() => run(() => safety.qualify(sel.id, {
                  severity: 'BENIN', benNoWageLoss: q.b1, benNoIncapacity: q.b2,
                  benNoDoctor: q.b3, benCareOnSiteOnly: q.b4, note: q.note,
                }), `${sel.ref} inscrit au registre comme accident bénin.`)}
                style={{ ...btn(fourChecked ? C.ok : '#f1f5f9', fourChecked ? '#fff' : '#94a3b8'),
                         cursor: fourChecked ? 'pointer' : 'not-allowed' }}>
                Bénin — registre
              </button>
              {/* Les 4 conditions du bénin cochées + une autre issue = contradiction : on
                  l'empêche à l'écran (règle « pas de sélections contradictoires »). */}
              <button disabled={busy || fourChecked}
                title={fourChecked ? 'Les 4 conditions du bénin sont cochées : décochez celle qui ne s’applique pas' : ''}
                onClick={() => run(() => safety.qualify(sel.id, { severity: 'NON_BENIN', note: q.note }),
                  `${sel.ref} à déclarer à l'assureur — échéance dans 8 jours calendrier après l'accident.`)}
                style={{ ...btn(fourChecked ? '#f1f5f9' : C.warn, fourChecked ? '#94a3b8' : '#fff'),
                         cursor: fourChecked ? 'not-allowed' : 'pointer' }}>
                À déclarer à l'assureur
              </button>
              <button disabled={busy || fourChecked}
                title={fourChecked ? 'Les 4 conditions du bénin sont cochées : décochez celle qui ne s’applique pas' : ''}
                onClick={() => run(() => safety.qualify(sel.id, { severity: 'GRAVE', note: q.note }),
                  `${sel.ref} qualifié GRAVE — assureur sous 8 jours, rapport circonstancié sous 10 jours via Mensura.`)}
                style={{ ...btn(fourChecked ? '#f1f5f9' : C.crit, fourChecked ? '#94a3b8' : '#fff'),
                         cursor: fourChecked ? 'not-allowed' : 'pointer' }}>
                Grave
              </button>
              <button disabled={busy || fourChecked}
                title={fourChecked ? 'Les 4 conditions du bénin sont cochées : décochez celle qui ne s’applique pas' : ''}
                onClick={() => run(() => safety.qualify(sel.id, { severity: 'TRES_GRAVE', note: q.note }),
                  `${sel.ref} qualifié TRÈS GRAVE — notifier l'inspection immédiatement, puis assureur et rapport.`)}
                style={{ ...btn(fourChecked ? '#f1f5f9' : '#7f1d1d', fourChecked ? '#94a3b8' : '#fff'),
                         cursor: fourChecked ? 'not-allowed' : 'pointer' }}>
                Très grave
              </button>
            </div>

            {/* Doublon / erreur : la seule façon de clôturer une fiche non qualifiée, motif obligatoire. */}
            <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px dashed ${C.line}` }}>
              <label style={lbl}>Doublon ou erreur ? (clôture sans qualification)</label>
              <input style={{ ...inp, marginBottom: 8 }} value={act.reason}
                onChange={(e) => setAct((x) => ({ ...x, reason: e.target.value }))}
                placeholder="Par exemple : doublon de AT-2026-0004" />
              <button disabled={busy || act.reason.trim().length < 5} style={btn('#fff', C.ink)}
                title={act.reason.trim().length < 5 ? 'Indiquez d’abord le motif' : ''}
                onClick={() => run(() => safety.close(sel.id, { reason: act.reason, lock: true, doublon: true }),
                  `${sel.ref} clôturée comme doublon / erreur.`)}>
                Clôturer comme doublon / erreur
              </button>
            </div>
          </div>
        )}

        {/* ---------------- SUITES ---------------- */}
        {canManage && !sel.lockedAt && sel.severity !== 'A_QUALIFIER' && (
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.line}` }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Suites</div>

            {sel.status === 'REGISTRE_BENIN' && (
              <>
                <p style={{ fontSize: '0.84rem', color: C.mute, margin: '0 0 8px' }}>
                  Si l'état de la victime s'aggrave, la déclaration à l'assureur redevient
                  obligatoire — <b>dans les 8 jours calendrier</b> à compter d'aujourd'hui.
                </p>
                <button disabled={busy} style={btn(C.warn)}
                  onClick={() => run(() => safety.aggravate(sel.id, {}),
                    `Aggravation enregistrée — le délai de 8 jours court.`)}>
                  Déclarer une aggravation
                </button>
              </>
            )}

            {sel.severity === 'TRES_GRAVE' && !sel.graveNotifiedAt && (
              <div style={{ marginTop: 10 }}>
                <label style={lbl}>Accident très grave — notification immédiate à l'inspection</label>
                <button disabled={busy} style={btn('#7f1d1d')}
                  onClick={() => run(() => safety.inspection(sel.id, {}),
                    'Notification à l\'inspection enregistrée.')}>
                  Inspection notifiée
                </button>
              </div>
            )}

            {sel.insurerDeadline && !sel.insurerDeclaredAt && (
              <div style={{ marginTop: 10 }}>
                <label style={lbl}>Déclaration à l'assureur — échéance {String(sel.insurerDeadline).slice(0, 10)}</label>
                <input style={{ ...inp, marginBottom: 8 }} value={act.insurerRef}
                  onChange={(e) => setAct((x) => ({ ...x, insurerRef: e.target.value }))}
                  placeholder="N° de dossier communiqué par l'assureur" />
                <button disabled={busy} style={btn(C.brand)}
                  onClick={() => run(() => safety.insurer(sel.id, { insurerRef: act.insurerRef }),
                    'Déclaration à l\'assureur enregistrée.')}>
                  Déclaration assureur envoyée
                </button>
              </div>
            )}

            {sel.graveReportDue && !sel.graveReportSentAt && (
              <div style={{ marginTop: 10 }}>
                <label style={lbl}>Rapport circonstancié — échéance {String(sel.graveReportDue).slice(0, 10)}</label>
                <input style={{ ...inp, marginBottom: 8 }} value={act.reportNote}
                  onChange={(e) => setAct((x) => ({ ...x, reportNote: e.target.value }))}
                  placeholder="Interlocuteur Mensura, canal d'envoi…" />
                <button disabled={busy} style={btn(C.brand)}
                  onClick={() => run(() => safety.reportSent(sel.id, { note: act.reportNote }),
                    'Rapport circonstancié marqué comme envoyé.')}>
                  Rapport envoyé
                </button>
              </div>
            )}

            <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px dashed ${C.line}` }}>
              <label style={lbl}>Clôturer (verrouille la fiche définitivement)</label>
              {/* Mêmes règles que le serveur : une obligation encore ouverte empêche la clôture
                  (une fiche close arrête tous les rappels). */}
              {manquesCloture(sel).length > 0 && (
                <p style={{ fontSize: '0.84rem', color: '#92400E', margin: '0 0 8px' }}>
                  Pour clôturer, il manque encore : <b>{manquesCloture(sel).join(', ')}</b>.
                </p>
              )}
              <input style={{ ...inp, marginBottom: 8 }} value={act.reason}
                onChange={(e) => setAct((x) => ({ ...x, reason: e.target.value }))}
                placeholder="Motif de clôture" />
              <button disabled={busy || manquesCloture(sel).length > 0} style={btn('#fff', C.ink)}
                onClick={() => run(() => safety.close(sel.id, { reason: act.reason, lock: true }),
                  `${sel.ref} clôturée et verrouillée.`)}>
                Clôturer et verrouiller
              </button>
            </div>
          </div>
        )}

        {sel.lockedAt && (
          <div style={{ marginTop: 12, background: '#f1f5f9', borderRadius: 6, padding: '0.6rem 0.7rem',
                        fontSize: '0.84rem', color: '#475569' }}>
            Fiche verrouillée le {String(sel.lockedAt).slice(0, 10)} — le registre est immuable.
            Une correction se fait par un nouvel événement tracé, pas en modifiant l'historique.
          </div>
        )}

        {/* ---------------- JOURNAL ---------------- */}
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.line}` }}>
          <div style={{ fontWeight: 700, marginBottom: 6, fontSize: '0.92rem' }}>
            Journal ({j.length}) — preuve de la chronologie
          </div>
          <div style={{ maxHeight: 210, overflowY: 'auto', fontSize: '0.8rem' }}>
            {j.map((e) => (
              <div key={e.id} style={{ display: 'flex', gap: 8, padding: '3px 0', color: C.mute }}>
                <span style={{ fontVariantNumeric: 'tabular-nums', minWidth: 118 }}>
                  {String(e.at).replace('T', ' ').slice(0, 16)}
                </span>
                <span style={{ fontWeight: 600, color: C.ink, minWidth: 148 }}>{EVENT_LABEL[e.kind] || e.kind}</span>
                <span>{e.actorName || e.actorUserId}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 980, margin: '0 auto' }}>
      <Breadcrumbs items={[{ label: 'Accidents & premiers soins' },
        { label: { scan: 'Déclarer', registre: 'Registre', secouristes: 'Secouristes & formations' }[tab] }]} />

      <h1 style={{ fontSize: '1.4rem', margin: '0 0 4px', color: C.brand }}>
        Accidents &amp; premiers soins
      </h1>
      <p style={{ margin: '0 0 1.1rem', color: C.mute, fontSize: '0.9rem' }}>
        {{
          scan: 'Touchez sur le plan l\u2019endroit de l\u2019accident, puis décrivez ce qui s\u2019est passé. En cas d\u2019accident grave, appelez le 112 avant tout.',
          registre: 'Registre des premiers soins — art. I.5-6 §3 du code du bien-être au travail. Données de santé : chaque consultation est tracée.',
          secouristes: 'Qui peut donner les premiers soins (art. I.5-6 §1) et jusqu’à quand (recyclage annuel de 4 h, art. I.5-9) ; les autres formations (cariste…) et leurs certificats.',
        }[tab]}
      </p>

      <div style={{ display: 'flex', gap: 4, marginBottom: '1.1rem', flexWrap: 'wrap' }}>
        {[['scan', 'Déclarer un accident'],
          ...(canRead ? [['registre', 'Registre']] : []),
          ...(canRead || canManage ? [['secouristes', 'Secouristes & formations']] : []),
        ].map(([k, l]) => (
          <button key={k} onClick={() => { setTab(k); setErr(null); }} style={{
            border: 'none', borderRadius: 6, padding: '0.5rem 0.9rem', cursor: 'pointer',
            fontWeight: 600, fontSize: '0.88rem',
            background: tab === k ? '#eef2ff' : 'transparent', color: tab === k ? '#4338ca' : '#475569',
          }}>{l}</button>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
          <a href="/securite" style={{
            fontSize: '0.84rem', color: C.mute, textDecoration: 'none',
            border: `1px solid ${C.line}`, borderRadius: 6, padding: '0.4rem 0.7rem',
          }}>
            Pas un accident ? → <b style={{ color: C.brand }}>Sécurité &amp; routines</b>
          </a>
        </div>
      </div>

      {err && (
        <div style={{ ...card, background: C.critBg, borderColor: '#f6cfcf', color: '#991B1B' }}>
          {err}
        </div>
      )}
      {flash && (
        <div style={{
          ...card,
          background: flash.kind === 'sos' ? C.critBg : C.okBg,
          borderColor: flash.kind === 'sos' ? '#f6cfcf' : C.okLine,
          color: flash.kind === 'sos' ? '#991B1B' : '#065F46',
          fontWeight: 600,
        }}>{flash.text}</div>
      )}

      {/* ============================ SCAN ============================ */}
      {tab === 'scan' && (
        <>
          {/* --- Où ? (24-10-2026 : plus de scan ; l'autocollant d'une trousse reste un raccourci) --- */}
          <div style={card}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Où est-ce arrivé ?</div>
            {asset ? (
              <div style={{ borderLeft: `4px solid ${C.brand}`, paddingLeft: 10, marginBottom: 8 }}>
                <div style={{ fontWeight: 700 }}>{asset.name}</div>
                <div style={{ color: C.mute, fontSize: '0.85rem' }}>
                  {asset.code}
                  {asset.locationText ? ` · ${asset.locationText}` : ' · emplacement non précisé'}
                  {asset.serialNumber ? ` · n° ${asset.serialNumber}` : ''}
                </div>
                <button type="button" onClick={() => setAsset(null)} style={{ border: 'none', background: 'none', padding: 0, marginTop: 4,
                  color: C.brand, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>
                  Ce n’est pas ici : choisir sur le plan
                </button>
              </div>
            ) : (
              <AccidentsLieuPlan lieu={lieu} onChoisir={(l) => {
                // Le texte proposé remplace le précédent texte PROPOSÉ, jamais ce que la personne a écrit.
                // `null` = point effacé (changement de plan).
                setForm((f) => ({ ...f, locationText: !f.locationText || f.locationText === lieu?.texte ? (l?.texte || '') : f.locationText }));
                setLieu(l);
              }} />
            )}
            <label style={{ ...lbl, marginTop: 10 }}>Précision du lieu{asset || lieu ? ' (facultatif)' : ''}</label>
            <input style={inp} value={form.locationText} maxLength={200} onChange={set('locationText')}
              placeholder="ex. allée 4, près du quai 2" />
          </div>

          {/* --- SOS : chemin séparé, aucune confirmation --- */}
          <div style={{ ...card, background: C.critBg, borderColor: '#f6cfcf' }}>
            <button onClick={doSos} disabled={busy} style={{ ...btn(C.crit), fontSize: '1.1rem', letterSpacing: '.04em' }}>
              SOS — alerter maintenant
            </button>
            <p style={{ margin: '9px 0 0', textAlign: 'center', fontSize: '0.82rem', color: C.crit, fontWeight: 700 }}>
              Ce bouton n'appelle PAS les secours. Appelez le 112 si nécessaire.
            </p>
          </div>

          {/* --- Déclaration --- */}
          <form onSubmit={doDeclare} style={card}>
            <div style={{ fontWeight: 700, marginBottom: '0.8rem' }}>
              {sosOuvert ? `Compléter le SOS ${sosOuvert.ref}` : 'Déclarer un accident / des premiers soins'}
            </div>

            {sosOuvert && (
              <div style={{ background: C.critBg, border: '1px solid #f6cfcf', borderRadius: 6,
                            padding: '0.6rem 0.7rem', fontSize: '0.85rem', color: '#991B1B', marginBottom: '0.9rem' }}>
                Votre SOS <b>{sosOuvert.ref}</b>
                {sosOuvert.sosAt ? ` (déclenché à ${new Date(sosOuvert.sosAt).toLocaleTimeString('fr-BE', { hour: '2-digit', minute: '2-digit' })})` : ''}
                {' '}attend ses détails : ce formulaire le complète, il ne crée pas de seconde fiche.
                <button type="button" onClick={() => setSosOuvert(null)}
                  style={{ display: 'block', marginTop: 6, border: 'none', background: 'none', padding: 0,
                           color: C.brand, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>
                  Non, c'est un autre accident
                </button>
              </div>
            )}

            <div style={{ background: C.okBg, border: `1px solid ${C.okLine}`, borderRadius: 6, padding: '0.6rem 0.7rem', fontSize: '0.83rem', color: '#166534', marginBottom: '0.9rem' }}>
              <b>Déjà rempli :</b> {asset ? 'lieu (depuis le QR) · ' : ''}date et heure · victime = vous
            </div>

            <div style={{ marginBottom: '0.9rem' }}>
              <label style={{ fontSize: '0.9rem', display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="checkbox" checked={form.forOther} onChange={set('forOther')} />
                Je déclare pour quelqu'un d'autre
              </label>
            </div>

            {form.forOther && (
              <div style={{ display: 'grid', gap: '0.7rem', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', marginBottom: '0.9rem' }}>
                <div>
                  <label style={lbl}>Statut de la victime</label>
                  <select style={inp} value={form.victimStatus} onChange={set('victimStatus')}>
                    <option value="INTERNE">Interne</option>
                    <option value="INTERIMAIRE">Intérimaire</option>
                    <option value="TIERS">Tiers (visiteur, chauffeur…)</option>
                  </select>
                </div>
                <div>
                  <label style={lbl}>{form.victimStatus === 'TIERS' ? 'Nom de la victime' : 'Victime'}</label>
                  {form.victimStatus === 'TIERS' ? (
                    <input style={inp} value={form.victimExternName}
                      onChange={set('victimExternName')} placeholder="Nom, prénom" />
                  ) : (
                    <select style={inp} value={form.victimUserId} onChange={set('victimUserId')}>
                      <option value="">— choisir la personne —</option>
                      {/* « pour quelqu'un d'autre » : soi-même n'est pas proposé (contradiction). */}
                      {people.filter((u) => u.id !== user?.id)
                        .map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                  )}
                </div>
              </div>
            )}

            {/* 1° — qui a donné les premiers soins */}
            <div style={{ marginBottom: '0.9rem' }}>
              <label style={lbl}>Qui a donné les premiers soins&nbsp;?</label>
              {form.selfTreated ? (
                <div style={{ fontSize: '0.85rem', color: C.mute, fontStyle: 'italic' }}>
                  Pas de secouriste à désigner : la personne s'est soignée elle-même.
                </div>
              ) : aidersWarning ? (
                <div style={{ background: C.warnBg, border: '1px solid #f0dcb4', borderRadius: 6, padding: '0.6rem 0.7rem', fontSize: '0.83rem', color: '#92400E' }}>
                  {aidersWarning} Seule l'option « je me suis soigné moi-même » est
                  possible tant que la formation n'est pas faite.
                </div>
              ) : (
                <select style={inp} value={form.firstAiderUserId} onChange={set('firstAiderUserId')} disabled={form.selfTreated}>
                  <option value="">— choisir un secouriste —</option>
                  {aiders.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              )}
              <label style={{ fontSize: '0.9rem', display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                <input type="checkbox" checked={form.selfTreated} onChange={set('selfTreated')} />
                Je me suis soigné moi-même
              </label>
            </div>

            <div style={{ marginBottom: '0.9rem' }}>
              <label style={lbl}>Que s'est-il passé&nbsp;? *</label>
              <textarea style={{ ...inp, minHeight: 70 }} required value={form.description}
                onChange={set('description')} placeholder="Coupure au doigt en ouvrant un carton…" />
            </div>

            {refine && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 10, flexWrap: 'wrap', background: '#f5f2f7',
                border: `1px solid ${C.line}`, borderRadius: 6,
                padding: '0.5rem 0.7rem', fontSize: '0.82rem', color: C.ink,
                margin: '0 0 0.7rem',
              }}>
                <span>
                  Listes réduites à ce qui correspond à <b>{form.injuryNature}</b>
                  {beninExclu && (
                    <b style={{ color: C.crit }}> — cette lésion exclut normalement l'accident bénin</b>
                  )}
                </span>
                <button type="button" onClick={() => setShowAll((v) => !v)}
                  style={{
                    border: `1px solid ${C.line}`, background: '#fff', borderRadius: 6,
                    padding: '3px 9px', fontSize: '0.78rem', cursor: 'pointer', color: C.brand,
                    fontWeight: 600, whiteSpace: 'nowrap',
                  }}>
                  {showAll ? 'Revenir aux suggestions' : 'Tout afficher'}
                </button>
              </div>
            )}

            <div style={{ display: 'grid', gap: '0.7rem', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))' }}>
              <div>
                <label style={lbl}>Nature de la lésion</label>
                <input style={inp} list="dl-injuryNature" value={form.injuryNature}
                  onChange={(e) => { setShowAll(false); set('injuryNature')(e); }}
                  placeholder="Choisir ou écrire…" />
                <datalist id="dl-injuryNature">{(vocab.injuryNature || []).map((v) => <option key={v} value={v} />)}</datalist>
              </div>
              <div>
                <label style={lbl}>Siège de la lésion{isRefined('injurySite') && <span style={{ color: C.brand }}> · liste réduite</span>}</label>
                <input style={inp} list="dl-injurySite" value={form.injurySite}
                  onChange={set('injurySite')} placeholder="Choisir ou écrire…" />
                <datalist id="dl-injurySite">{opts('injurySite').map((v) => <option key={v} value={v} />)}</datalist>
              </div>
              <div>
                <label style={lbl}>Soins donnés{isRefined('careGiven') && <span style={{ color: C.brand }}> · liste réduite</span>}</label>
                <input style={inp} list="dl-careGiven" value={form.careGiven}
                  onChange={set('careGiven')} placeholder="Choisir ou écrire…" />
                <datalist id="dl-careGiven">{opts('careGiven').map((v) => <option key={v} value={v} />)}</datalist>
              </div>
              <div>
                <label style={lbl}>Suites{isRefined('followUp') && <span style={{ color: C.brand }}> · liste réduite</span>}</label>
                <input style={inp} list="dl-followUp" value={form.followUp}
                  onChange={set('followUp')} placeholder="Choisir ou écrire…" />
                <datalist id="dl-followUp">{opts('followUp').map((v) => <option key={v} value={v} />)}</datalist>
              </div>
            </div>

            <div style={{ margin: '0.9rem 0' }}>
              <label style={lbl}>Circonstances</label>
              <input style={inp} list="dl-circumstances" value={form.circumstances}
                onChange={set('circumstances')} placeholder="Choisir une situation courante, ou décrire" />
              <datalist id="dl-circumstances">{(vocab.circumstances || []).map((v) => <option key={v} value={v} />)}</datalist>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={lbl}>Témoins</label>
              <input style={inp} list="dl-witnesses" value={form.witnesses}
                onChange={set('witnesses')} placeholder="Noms, ou « Aucun »" />
              <datalist id="dl-witnesses">
                {[...(vocab.witnesses || []), ...people.map((u) => u.name)].map((v) => <option key={v} value={v} />)}
              </datalist>
              <p style={{ margin: '5px 0 0', fontSize: '0.78rem', color: C.mute }}>
                Mention obligatoire (art. I.5-6 §3, 5°) — absente de la fiche Excel actuelle.
              </p>
            </div>

            <button type="submit" disabled={busy} style={btn(C.brand)}>
              {busy ? 'Enregistrement…' : (sosOuvert ? `Compléter le SOS ${sosOuvert.ref}` : 'Enregistrer au registre')}
            </button>
          </form>
        </>
      )}

      {/* ============================ REGISTRE ============================ */}
      {tab === 'registre' && canRead && printing && (
        <SecuriteRegistrePrint
          rows={list}
          focus={printing.focus || null}
          printedBy={user?.name || user?.username || null}
          onClose={() => setPrinting(null)}
        />
      )}

      {tab === 'registre' && canRead && !printing && (
        <>
          <div style={{ ...card, display: 'flex', gap: '.7rem', alignItems: 'center',
            flexWrap: 'wrap' }}>
            <div style={{ fontSize: '.88rem', color: C.ink }}>
              <b>{list.length}</b> declaration(s) affichee(s)
            </div>
            <p style={{ margin: 0, fontSize: '.8rem', color: C.mute, maxWidth: '54ch' }}>
              L'extrait papier porte la date d'extraction et votre nom : un
              registre imprime doit pouvoir se situer dans le temps.
            </p>
            <button type="button" disabled={!list.length}
              onClick={() => setPrinting({})}
              style={{
                marginLeft: 'auto', padding: '.5rem 1rem', borderRadius: 8,
                border: 'none', background: list.length ? C.brand : '#cbd5e1',
                color: '#fff', fontWeight: 600,
                cursor: list.length ? 'pointer' : 'default',
              }}>
              Imprimer / PDF
            </button>
          </div>
          {deadlines && (deadlines.insurer.length > 0 || deadlines.report.length > 0) && (
            <div style={{ ...card, borderLeft: `4px solid ${C.crit}` }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Échéances légales en cours</div>
              {deadlines.insurer.map((d) => (
                <div key={d.id} style={{ fontSize: '0.9rem', color: d.overdue ? C.crit : C.ink }}>
                  <b>{d.ref}</b> — déclaration assureur ·{' '}
                  {d.overdue ? `EN RETARD de ${-d.daysLeft} j` : `${d.daysLeft} j restants`} (8 jours)
                </div>
              ))}
              {deadlines.report.map((d) => (
                <div key={d.id} style={{ fontSize: '0.9rem', color: d.overdue ? C.crit : C.ink }}>
                  <b>{d.ref}</b> — rapport circonstancié ·{' '}
                  {d.overdue ? `EN RETARD de ${-d.daysLeft} j` : `${d.daysLeft} j restants`} (10 jours)
                </div>
              ))}
            </div>
          )}

          {fiche()}

          <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem' }}>
                <thead>
                  <tr style={{ background: C.bg, textAlign: 'left' }}>
                    {['Réf.', 'Date', 'Victime', 'Lésion', 'Lieu', 'Gravité', 'Statut'].map((h) => (
                      <th key={h} style={{ padding: '9px 10px', borderBottom: `1px solid ${C.line}`, color: C.mute, fontSize: '0.76rem', textTransform: 'uppercase', letterSpacing: '.04em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {list.length === 0 && (
                    <tr><td colSpan={7} style={{ padding: '1.2rem', textAlign: 'center', color: C.mute }}>
                      Aucune inscription au registre.
                    </td></tr>
                  )}
                  {list.map((r) => (
                    <tr key={r.id} onClick={() => openFiche(r.id)} title="Ouvrir la fiche"
                      style={{
                        borderBottom: '1px solid #f1f5f9', cursor: 'pointer',
                        background: sel?.id === r.id ? '#f5f2f7' : 'transparent',
                      }}>
                      <td style={{ padding: '9px 10px', fontWeight: 700, whiteSpace: 'nowrap' }}>{r.ref}</td>
                      <td style={{ padding: '9px 10px', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                        {String(r.occurredAt).slice(0, 10)}
                        <span style={{ color: C.mute }}> {String(r.occurredAt).slice(11, 16)}</span>
                      </td>
                      <td style={{ padding: '9px 10px' }}>
                        {r.victimName || r.victimExternName || r.victimUserId || '—'}
                        {r.victimStatus && r.victimStatus !== 'INTERNE' && (
                          <span style={{ color: C.mute, fontSize: '0.78rem' }}> ({r.victimStatus.toLowerCase()})</span>
                        )}
                      </td>
                      <td style={{ padding: '9px 10px' }}>
                        {r.injuryNature || '—'}{r.injurySite ? ` · ${r.injurySite}` : ''}
                      </td>
                      <td style={{ padding: '9px 10px', color: C.mute }}>
                        {r.asset ? `${r.asset.code}` : (r.locationText || (r.planId ? LIBELLE_POINT : '—'))}
                      </td>
                      <td style={{ padding: '9px 10px', fontWeight: 700, color: (SEVERITY[r.severity] || {}).fg }}>
                        {(SEVERITY[r.severity] || {}).label || r.severity}
                      </td>
                      <td style={{ padding: '9px 10px' }}><Badge map={STATUS} value={r.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p style={{ fontSize: '0.8rem', color: C.mute, marginTop: '0.6rem' }}>
            Cliquez sur une ligne pour ouvrir la fiche{canManage ? ', la qualifier et la clôturer' : ''}.
            Chaque consultation est journalisée : le registre contient des données de santé.
            {!canManage && ' Lecture seule.'}
          </p>
        </>
      )}

      {/* ========================= SECOURISTES ========================= */}
      {tab === 'secouristes' && (canRead || canManage) && <AccidentsSecouristes canManage={canManage} />}
    </div>
  );
}
